'use client'

import { useMemo } from 'react'
import type { Marble } from 'src/types/domain/Marble'
import type { Bet } from 'src/types/domain/Bet'
import type { BetType } from 'src/types/domain/BetDetails'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { sideBetMultiplier } from 'src/utils/effectiveRtp'
import { WinPill } from './WinPill'

// Forecast / tricast odds come from the shared `effectiveRtp` module,
// which carries each game's own margins from its DynamoDB MarginConfig.
// The flat 0.85 that used to live here was Marble Plinko's override
// applied to every game, so Rolling Dunes' tiles quoted 285.6x on a
// tricast the backend actually pays 288.96x on.
//
// That module is dependency-free by design — `calculationService.ts`
// pulls in `node:assert`, which Next.js can't bundle for the browser.
function calcForecastTricastMultiplier(
  betType: BetType,
  totalItems: number,
  gameKey: string | null
): number {
  switch (betType) {
    case 'StraightForecast':
    case 'ReverseForecast':
    case 'Tricast':
    case 'CombinationTricast':
      return sideBetMultiplier(gameKey, betType, totalItems)
    default:
      return 0
  }
}

// One placed-chip view (matches the parent panel's PlacedChip without
// importing it — keeps the component file self-contained, with only
// the fields this component reads).
type PlacedChipView = Readonly<{
  optionId: string
  amount: number
}>

export interface SideBetPlacement {
  optionId: string
  marketId: string
  betType: BetType
  selectedItems: string[]
  multiplier: number
  label: string
}

interface SideBetRow {
  optionId: string
  betType: BetType
  // Variant flag drives the row's icon + class rather than the label —
  // the label uses the constituent marbles' colours so the player can
  // read the row without tracking which is m1/m2/m3.
  variant: 'exact' | 'any'
  marbleIds: string[]
  multiplier: number
  // Pre-formatted label (e.g. "Yellow → Red"). Stored on the chip too.
  label: string
}

type SubmissionState = 'placing' | 'confirmed' | 'failed'

interface Props {
  marbles: Marble[]
  totalMarbles: number
  // Pricing key (from the race-configuration id) so side-bet odds use
  // this game's own margins. Null for a game with no verified pricing,
  // in which case the platform defaults apply.
  gameKey: string | null
  // How many finishing positions the game submits at settlement
  // (`game.resultsToSubmit`). Forecast / tricast settle against
  // positions 1+2 / 1+2+3, so games that only return a single result
  // (Stairpong, Coin Flip) can't host these side-bets at all — we
  // gate the row generation on this so Plinko-style multi-result
  // games keep them and single-result games hide the rail entirely.
  maxResultPositions: number
  // Per-bet selection cap (`game.maxPickableMarbles`). Side-bets
  // submit a multi-marble payload (forecast=2, tricast=3) so a game
  // that only allows 2-marble bets must hide the tricast tiles —
  // otherwise the bet rejects with `UnknownError` at register time.
  maxPickableMarbles: number
  // Marble IDs in the order the player placed chips on them in the main
  // market. Drives which side-bet rows appear (2 → forecast, 3+ →
  // forecast on first-2 + tricast on first-3).
  marbleOrder: string[]
  // Used so the chip we create carries the main market's ID even though
  // its `optionId` is synthetic (sidebet-...).
  mainMarketId: string
  placedChips: Record<string, PlacedChipView>
  betsSubmitted: boolean
  isSettled: boolean
  // Race result mapping (marbleId → finishing position). Drives the
  // settle-time visualisation: matched marbles get a green ring,
  // tiles whose bet lost dim. Empty map until settle.
  placementByMarbleId: Map<string, number>
  // Synthetic-optionId → settled bet, so the row can show the actual
  // payout the player received on a winning side-bet (mirrors the main
  // option's WinPill behaviour). Bets in here may be in any status
  // (won/lost/pending) — the row renders accordingly.
  myBetByOptionId: Map<string, Bet>
  currency: string
  // Active multipliers folded into the displayed pays value so the
  // pill matches what the bet would actually pay out. Mirrors the
  // main option's lightning + booster + level treatment.
  lightningBoostApplies: boolean
  lightningBoostedItems: Set<string>
  lightningBoostPercent: number
  levelBoostFactor: number
  appliedBoosters: Record<string, { boosterId: string; percent: number }>
  // Stagger anchor for the side-bet WinPills at settle. Side-bets
  // animate in *after* the main option WinPills so the cascade reads
  // as "main marbles celebrate, then your combo bets celebrate" —
  // not all at once.
  winPillBaseDelayMs: number
  submissionStates: Record<string, SubmissionState>
  freshChips: Record<string, true>
  onPlace: (placement: SideBetPlacement) => void
  onRemove: (optionId: string) => void
  // Helpers from parent so visuals stay consistent across the panel.
  getChipStyleForAmount: (amount: number) => { bg: string; border: string }
  formatAmount: (amount: number) => string
}

// Did the marble at index `idx` of the side-bet match its expected
// finishing position given the bet variant?
//   - exact: the i-th marble must be the i-th finisher (positions 1, 2,
//     3 indexed from 1).
//   - any: the marble must be in the top-N finishers, where N is the
//     bet's selection length.
function isMarbleMatch(
  marbleId: string,
  idx: number,
  selectedLen: number,
  variant: 'exact' | 'any',
  placementByMarbleId: Map<string, number>
): boolean {
  const pos = placementByMarbleId.get(marbleId)
  if (pos === undefined) return false
  return variant === 'exact' ? pos === idx + 1 : pos >= 1 && pos <= selectedLen
}

function buildLabel(
  marbleIds: string[],
  marbleById: Map<string, Marble>,
  variant: 'exact' | 'any'
): string {
  const colors = marbleIds.map((id) => {
    const m = marbleById.get(id)
    if (!m) return '?'
    return m.color.charAt(0).toUpperCase() + m.color.slice(1).toLowerCase()
  })
  const sep = variant === 'exact' ? ' → ' : ' + '
  return colors.join(sep)
}

export function SideBetRows({
  marbles,
  totalMarbles,
  gameKey,
  maxResultPositions,
  maxPickableMarbles,
  marbleOrder,
  mainMarketId,
  placedChips,
  betsSubmitted,
  isSettled,
  placementByMarbleId,
  myBetByOptionId,
  currency,
  lightningBoostApplies,
  lightningBoostedItems,
  lightningBoostPercent,
  levelBoostFactor,
  appliedBoosters,
  winPillBaseDelayMs,
  submissionStates,
  freshChips,
  onPlace,
  onRemove,
  getChipStyleForAmount,
  formatAmount
}: Props) {
  const currencySymbol = getCurrencySymbol(currency)
  // O(1) marble lookup. The render path below resolves a marble for
  // each tile's marbleIds entry, and `buildLabel` does it again for
  // every row — `find()` was O(marbles) per call. Building the map
  // once per `marbles` change keeps the per-tile work flat.
  const marbleById = useMemo<Map<string, Marble>>(() => {
    const m = new Map<string, Marble>()
    for (const marble of marbles) m.set(marble.uuid, marble)
    return m
  }, [marbles])
  const rows = useMemo<SideBetRow[]>(() => {
    if (marbleOrder.length < 2 || totalMarbles < 2) return []
    // Forecast needs ≥ 2 finishing positions; without that the bet
    // can never settle. Single-result games (Stairpong, Coin Flip)
    // skip the entire rail — there's nothing meaningful to show.
    if (maxResultPositions < 2) return []
    // The game must also accept multi-marble bets at register time
    // (`maxPickableMarbles`). A 1-marble cap means no forecast or
    // tricast can be submitted at all → no rail.
    if (maxPickableMarbles < 2) return []

    const out: SideBetRow[] = []
    const [m1, m2, m3] = marbleOrder
    if (!m1 || !m2) return out

    const fcExact = calcForecastTricastMultiplier('StraightForecast', totalMarbles, gameKey)
    const fcAny = calcForecastTricastMultiplier('ReverseForecast', totalMarbles, gameKey)

    out.push({
      optionId: `sidebet-StraightForecast-${m1}-${m2}`,
      betType: 'StraightForecast',
      variant: 'exact',
      marbleIds: [m1, m2],
      multiplier: fcExact,
      label: buildLabel([m1, m2], marbleById, 'exact')
    })
    out.push({
      optionId: `sidebet-ReverseForecast-${m1}-${m2}`,
      betType: 'ReverseForecast',
      variant: 'any',
      marbleIds: [m1, m2],
      multiplier: fcAny,
      label: buildLabel([m1, m2], marbleById, 'any')
    })

    if (m3 && totalMarbles >= 3 && maxResultPositions >= 3 && maxPickableMarbles >= 3) {
      const tcExact = calcForecastTricastMultiplier('Tricast', totalMarbles, gameKey)
      const tcAny = calcForecastTricastMultiplier('CombinationTricast', totalMarbles, gameKey)

      out.push({
        optionId: `sidebet-Tricast-${m1}-${m2}-${m3}`,
        betType: 'Tricast',
        variant: 'exact',
        marbleIds: [m1, m2, m3],
        multiplier: tcExact,
        label: buildLabel([m1, m2, m3], marbleById, 'exact')
      })
      out.push({
        optionId: `sidebet-CombinationTricast-${m1}-${m2}-${m3}`,
        betType: 'CombinationTricast',
        variant: 'any',
        marbleIds: [m1, m2, m3],
        multiplier: tcAny,
        label: buildLabel([m1, m2, m3], marbleById, 'any')
      })
    }

    return out
  }, [marbleOrder, totalMarbles, gameKey, maxResultPositions, maxPickableMarbles, marbleById])

  // Once the player's bets have left for the round, only the side-bet
  // tiles they actually staked on stay visible — derived rows that no
  // chip is sitting on are reference clutter while the race runs and
  // post-settle. Filtering at the array level (instead of the
  // previous "render every row + CSS-hide" approach) keeps the DOM
  // free of zero-width siblings whose flex-basis interactions made
  // the 50/50 layout fight with the wrapper's `gap`. Trade-off: no
  // leave-animation when a row collapses out — the panel just
  // re-renders without it. Cleaner and more deterministic.
  const visibleRows = rows.filter((r) => !betsSubmitted || !!placedChips[r.optionId])
  // When no rows survive the filter the wrapper itself collapses —
  // drives the rail's slide-out at race start when the player has no
  // side-bet stakes.
  const collapsed = visibleRows.length === 0

  // How many tiles will visibly render right now — drives the mobile
  // grid layout (`data-count`).
  const visibleCount = visibleRows.length
  // The "solo on its own row" pill — the last visible pill, when the
  // visible count is odd (1 or 3). Drives the `--solo` modifier so
  // the lonely pill renders at ~60% width centred, instead of
  // stretching to fill the row.
  const soloVisibleIdx = visibleCount % 2 === 1 ? visibleCount - 1 : -1

  return (
    <div
      className={['chip-panel__sidebets', collapsed ? 'chip-panel__sidebets--collapsed' : '']
        .filter(Boolean)
        .join(' ')}
      data-count={visibleCount}
      aria-label="Side bets"
      aria-hidden={collapsed}
    >
      {visibleRows.map((row, idx) => {
        const chip = placedChips[row.optionId]
        const placed = !!chip
        const chipStyle = chip ? getChipStyleForAmount(chip.amount) : null
        const submission = submissionStates[row.optionId]
        const fresh = !!freshChips[row.optionId]

        // Live multiplier shown on the pill — folds in lightning,
        // inventory booster, and level-system boost so the pill
        // matches what the backend will actually pay out. Lightning
        // applies when ANY of the side-bet's selected marbles is in
        // this round's boosted set (mirrors the main option rule
        // applied per-marble). Booster + level apply uniformly.
        const isBoosted =
          lightningBoostApplies && row.marbleIds.some((id) => lightningBoostedItems.has(id))
        const optionBoosterPercent = appliedBoosters[row.optionId]?.percent ?? 0
        const displayedMultiplier =
          row.multiplier *
          (isBoosted ? 1 + lightningBoostPercent / 100 : 1) *
          (optionBoosterPercent > 0 ? 1 + optionBoosterPercent / 100 : 1) *
          levelBoostFactor

        // Per-marble + overall outcome at settle. Computed regardless
        // of whether a chip was placed so the marble dots can carry
        // their match indicator (helps players see "I got 1 of 3
        // right" even when reading the tile from the side-bet row).
        // Tile-level won/lost styling only kicks in when the player
        // had a chip on the row though — dimming an unplaced tile
        // would be noise.
        const matches = isSettled
          ? row.marbleIds.map((mid, idx) =>
              isMarbleMatch(mid, idx, row.marbleIds.length, row.variant, placementByMarbleId)
            )
          : null
        const won = !!matches && matches.every((m) => m)
        const lostWithChip = isSettled && placed && !won
        const wonWithChip = isSettled && placed && won

        // Use the persisted bet's payout when settled — mirrors what
        // the main option's WinPill does. `potentialPayout` is already
        // in major currency units (matches how WinPill consumes it on
        // the main option), so no `fromInteger` conversion. Falls back
        // to chip × multiplier if the bet hasn't propagated yet (race
        // condition between the settled event and the bet store).
        const myBet = myBetByOptionId.get(row.optionId)
        const wonAmount = wonWithChip
          ? myBet?.potentialPayout && myBet.potentialPayout > 0
            ? myBet.potentialPayout
            : (chip?.amount ?? 0) * displayedMultiplier
          : 0

        return (
          <button
            key={row.optionId}
            type="button"
            className={[
              'chip-panel__sidebet',
              placed ? 'chip-panel__sidebet--placed' : '',
              betsSubmitted ? 'chip-panel__sidebet--locked' : '',
              row.variant === 'exact' ? 'chip-panel__sidebet--exact' : 'chip-panel__sidebet--any',
              row.marbleIds.length === 3 ? 'chip-panel__sidebet--triple' : '',
              wonWithChip ? 'chip-panel__sidebet--won' : '',
              lostWithChip ? 'chip-panel__sidebet--lost' : '',
              idx === soloVisibleIdx ? 'chip-panel__sidebet--solo' : ''
            ]
              .filter(Boolean)
              .join(' ')}
            disabled={betsSubmitted}
            onClick={() => {
              if (betsSubmitted) return
              onPlace({
                optionId: row.optionId,
                marketId: mainMarketId,
                betType: row.betType,
                selectedItems: row.marbleIds,
                multiplier: row.multiplier,
                label: row.label
              })
            }}
            aria-label={`${row.label} (${row.variant === 'exact' ? 'Exact Order' : 'Any Order'}) at x${displayedMultiplier.toFixed(2)}`}
          >
            <div className="chip-panel__sidebet-marbles">
              {row.marbleIds.map((mid, idx) => {
                const marble = marbleById.get(mid)
                const color = marble?.color?.toLowerCase() ?? '#666'
                const matched = matches?.[idx] ?? false
                return (
                  <div
                    key={`${mid}-${idx}`}
                    className={[
                      'chip-panel__sidebet-marble',
                      isSettled
                        ? matched
                          ? 'chip-panel__sidebet-marble--match'
                          : 'chip-panel__sidebet-marble--miss'
                        : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={{
                      background: marble?.image
                        ? `center / cover no-repeat url(${marble.image})`
                        : color
                    }}
                  >
                    {row.variant === 'exact' && (
                      <span className="chip-panel__sidebet-marble-rank">{idx + 1}</span>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="chip-panel__sidebet-pays">{displayedMultiplier.toFixed(2)}x</div>
            {/* Win celebration matches the main option's WinPill so
                payouts read identically across the panel. The pill
                sits above the tile, the chip stays in the corner
                showing the stake, and the multiplier in the body
                stays put so the player can read "got this multiplier
                on this stake → won this much" all at once. */}
            {wonWithChip && (
              <WinPill
                isPayout
                payoutAmount={wonAmount}
                currencySymbol={currencySymbol}
                // Side-bet wins always render at the base 'win' tier
                // — tier scaling is reserved for the TOTAL WIN banner
                // so the panel has one element that visibly upsizes
                // for big rounds. Keeping every side-bet pill the
                // same size means a row of 4 winning side-bets reads
                // as a coherent strip instead of a competition for
                // attention.
                tier="win"
                // Cascade: each side-bet pill animates in 80ms after
                // its predecessor, anchored after the main option
                // wins finish (`winPillBaseDelayMs`). So a player
                // hitting a tricast sees: main marble winners pop →
                // side-bets cascade → TOTAL WIN banner caps it.
                delayMs={winPillBaseDelayMs + idx * 80}
              />
            )}
            {/* Chip slot is always rendered (empty when no stake) so
                the pill's width is the same whether or not a chip
                sits in it — adding/removing a chip doesn't shift the
                pill's content-sized width on desktop, and adjacent
                pills don't jump as the player places stakes. */}
            <div className="chip-panel__sidebet-chip-slot" aria-hidden={!placed}>
              {placed && chipStyle && (
                <div
                  className={[
                    'chip-panel__opt-chip',
                    'chip-panel__opt-chip--top',
                    'chip-panel__opt-chip--sidebet',
                    fresh ? 'chip-panel__opt-chip--entering' : '',
                    submission === 'confirmed' ? 'chip-panel__opt-chip--confirmed' : '',
                    submission === 'failed' ? 'chip-panel__opt-chip--failed' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ background: chipStyle.bg, borderColor: chipStyle.border }}
                >
                  <span className="chip-panel__opt-chip-amount">{formatAmount(chip.amount)}</span>
                  {!betsSubmitted && !isSettled && (
                    <div
                      className="chip-panel__opt-chip-x"
                      onClick={(e) => {
                        e.stopPropagation()
                        onRemove(row.optionId)
                      }}
                    >
                      ✕
                    </div>
                  )}
                </div>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}
